/**
 * React Query hooks for the User Secret Vault.
 *
 * All paths are passed relative to the api-client, which auto-prefixes
 * them with `/api/v1/tenants/{currentTenantId}/`. That aligns with the
 * tenant-scoped `secrets_router` mounted in api/src/main.py.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';
import type {
  Grant,
  GrantCreateInput,
  GrantListResponse,
  GrantPermission,
  GrantSubjectType,
  Secret,
  SecretCreateInput,
  SecretListResponse,
  SecretRotateInput,
  SecretUpdateInput,
  SecretVersionListResponse,
} from '../types';

// =============================================================================
// Query Keys
// =============================================================================

export const secretKeys = {
  all: (tenantId: string | undefined) => ['secrets', tenantId] as const,
  mine: (tenantId: string | undefined, params: Record<string, unknown>) =>
    [...secretKeys.all(tenantId), 'mine', params] as const,
  sharedWithMe: (tenantId: string | undefined, params: Record<string, unknown>) =>
    [...secretKeys.all(tenantId), 'shared-with-me', params] as const,
  sharedByMe: (tenantId: string | undefined, params: Record<string, unknown>) =>
    [...secretKeys.all(tenantId), 'shared-by-me', params] as const,
  versions: (tenantId: string | undefined, secretId: string) =>
    [...secretKeys.all(tenantId), 'versions', secretId] as const,
  shares: (tenantId: string | undefined, secretId: string) =>
    [...secretKeys.all(tenantId), 'shares', secretId] as const,
};

// =============================================================================
// API Functions
// =============================================================================

async function fetchMySecrets(params: Record<string, unknown>): Promise<SecretListResponse> {
  const { data } = await apiClient.get<SecretListResponse>('/secrets/mine', { params });
  return data;
}

async function fetchSharedWithMe(params: Record<string, unknown>): Promise<SecretListResponse> {
  const { data } = await apiClient.get<SecretListResponse>('/secrets/shared-with-me', { params });
  return data;
}

async function fetchSharedByMe(params: Record<string, unknown>): Promise<SecretListResponse> {
  const { data } = await apiClient.get<SecretListResponse>('/secrets/shared-by-me', { params });
  return data;
}

async function fetchVersions(secretId: string): Promise<SecretVersionListResponse> {
  const { data } = await apiClient.get<SecretVersionListResponse>(`/secrets/${secretId}/versions`);
  return data;
}

async function createSecret(input: SecretCreateInput): Promise<Secret> {
  const { data } = await apiClient.post<Secret>('/secrets', input);
  return data;
}

async function updateSecret(id: string, input: SecretUpdateInput): Promise<Secret> {
  const { data } = await apiClient.patch<Secret>(`/secrets/${id}`, input);
  return data;
}

async function rotateSecret(id: string, input: SecretRotateInput): Promise<void> {
  await apiClient.put(`/secrets/${id}/value`, input);
}

async function deleteSecret(id: string): Promise<void> {
  await apiClient.delete(`/secrets/${id}`);
}

async function revealSecret(id: string, reason?: string): Promise<string> {
  const { data } = await apiClient.post<{ value: string }>(`/secrets/${id}/reveal`, {
    reason: reason?.trim() || null,
  });
  return data.value;
}

async function fetchShares(secretId: string): Promise<GrantListResponse> {
  const { data } = await apiClient.get<GrantListResponse>(`/secrets/${secretId}/shares`);
  return data;
}

async function addShare(secretId: string, input: GrantCreateInput): Promise<Grant> {
  const { data } = await apiClient.post<Grant>(`/secrets/${secretId}/shares`, input);
  return data;
}

async function removeShare(
  secretId: string,
  subjectType: GrantSubjectType,
  subjectId: string,
  permission?: GrantPermission,
): Promise<void> {
  const query = permission ? `?permission=${permission}` : '';
  await apiClient.delete(`/secrets/${secretId}/shares/${subjectType}/${subjectId}${query}`);
}

// =============================================================================
// Query Hooks
// =============================================================================

export function useMySecrets(params: Record<string, unknown> = {}) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: secretKeys.mine(tenantId, params),
    queryFn: () => fetchMySecrets(params),
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

export function useSharedWithMe(params: Record<string, unknown> = {}) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: secretKeys.sharedWithMe(tenantId, params),
    queryFn: () => fetchSharedWithMe(params),
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

export function useSharedByMe(params: Record<string, unknown> = {}) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: secretKeys.sharedByMe(tenantId, params),
    queryFn: () => fetchSharedByMe(params),
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

export function useSecretShares(secretId: string | null) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: secretKeys.shares(tenantId, secretId ?? ''),
    queryFn: () => fetchShares(secretId as string),
    enabled: !!tenantId && !!secretId,
  });
}

export function useSecretVersions(secretId: string | null) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: secretKeys.versions(tenantId, secretId ?? ''),
    queryFn: () => fetchVersions(secretId as string),
    enabled: !!tenantId && !!secretId,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

function useInvalidateLists() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return () => queryClient.invalidateQueries({ queryKey: secretKeys.all(tenantId) });
}

export function useCreateSecret() {
  const invalidate = useInvalidateLists();
  return useMutation({
    mutationFn: (input: SecretCreateInput) => createSecret(input),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateSecret() {
  const invalidate = useInvalidateLists();
  return useMutation({
    mutationFn: (args: { id: string; input: SecretUpdateInput }) =>
      updateSecret(args.id, args.input),
    onSuccess: () => invalidate(),
  });
}

export function useRotateSecret() {
  const invalidate = useInvalidateLists();
  return useMutation({
    mutationFn: (args: { id: string; input: SecretRotateInput }) =>
      rotateSecret(args.id, args.input),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteSecret() {
  const invalidate = useInvalidateLists();
  return useMutation({
    mutationFn: (id: string) => deleteSecret(id),
    onSuccess: () => invalidate(),
  });
}

/**
 * Reveal the decrypted value of a secret (ISS-473).
 * Requires Secret#view_metadata on the backend; the mutation resolves to the value
 * or rejects with the backend error (403/429) for the UI to handle.
 */
export function useRevealSecret() {
  return useMutation({
    mutationFn: (args: { id: string; reason?: string }) =>
      revealSecret(args.id, args.reason),
  });
}

export function useAddShare() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useMutation({
    mutationFn: (args: { secretId: string; input: GrantCreateInput }) =>
      addShare(args.secretId, args.input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: secretKeys.shares(tenantId, variables.secretId),
      });
    },
  });
}

export function useRemoveShare() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useMutation({
    mutationFn: (args: {
      secretId: string;
      subjectType: GrantSubjectType;
      subjectId: string;
      permission?: GrantPermission;
    }) => removeShare(args.secretId, args.subjectType, args.subjectId, args.permission),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: secretKeys.shares(tenantId, variables.secretId),
      });
    },
  });
}
